import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { cronKeys, dayOneKeys, feedKeys } from '~/lib/query-keys';
import { calibrationKeys } from '~/hooks/queries/use-calibration';
import { perfectDaySpecKeys } from '~/hooks/queries/use-perfect-day-spec';

/**
 * FRONT-907: day-one activation mutations — seeding the onboarding chat at
 * wizard finish, deciding the starter-cron proposal card, and recording the
 * explicit exit (completed/abandoned) from the day-one conversation.
 */

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed?.error) return parsed.error;
    } catch {
      /* not JSON */
    }
    return err.message.slice(0, 200);
  }
  return fallback;
}

/**
 * FRONT-1232: stamp the wizard-completed marker — the signal that clears the
 * home feed's "Finish setting up Eddy" banner for good in its wizard phase.
 *
 * Called at the wizard's saves-succeeded boundary, deliberately BEFORE the
 * day-one seed: the seed 404s wherever the `eddy.day-one-chat` flag is off
 * by design — which a per-org rollback or a fail-closed Unleash error can
 * produce on a flag-ON environment too — so stamping after it would mean users who genuinely
 * finished are never marked done and get nagged back into a completed wizard.
 * The endpoint is unflagged and idempotent for the same reason.
 *
 * Errors are swallowed on purpose: failing to record the marker must never
 * block the hand-off into the app, and the worst case is one extra banner the
 * user can dismiss. It invalidates the feed so the banner disappears without
 * waiting for the background refetch.
 */
export function useMarkWizardCompleted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ wizardCompletedAt: string }>('/api/onboarding/wizard/completed', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.all }),
    onError: (err) =>
      console.warn('[onboard] could not record wizard completion:', errorMessage(err, 'unknown')),
  });
}

export function useSeedDayOne() {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-3223: the setup asked in the chat, saved as the chat's first
    // messages; `handover` when the person typed their own words mid-setup and
    // the live chat takes over (no scripted opening).
    mutationFn: (input?: { setupTranscript: Array<{ role: 'assistant' | 'user'; text: string }>; handover?: boolean }) =>
      api.post<{ sessionId: string; seeded: boolean }>('/api/onboarding/day-one/seed', input ?? {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: dayOneKeys.all }),
  });
}

/** A real, connectable mail/calendar provider candidate (FRONT-907 real
 * cron/connect flow) — mirrors the backend's MailProviderCandidate shape. */
export interface ProviderChoice {
  providerKey: string;
  providerName: string;
}

/** Response shape for the starter-cron approve call. The flow is no longer a
 * single round trip: the backend may ask which real provider to use, or
 * require a real connection, before it ever creates anything. */
export interface ApproveStarterCronsResult {
  status: 'accepted' | 'needs-provider-choice' | 'needs-connect';
  cronIds?: string[];
  needsAuth?: boolean;
  candidates?: ProviderChoice[];
  connect?: ProviderChoice;
}

export function useApproveStarterCrons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; providerKey?: string }) =>
      api.post<ApproveStarterCronsResult>('/api/onboarding/day-one/starter-crons/approve', input),
    onSuccess: (data) => {
      if (data?.status !== 'accepted') return;
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
      qc.invalidateQueries({ queryKey: cronKeys.all });
      // Same FRONT-666 contract as the Schedule page's create: an
      // unauthorized job won't fire until the user re-auths it.
      if (data?.needsAuth) {
        toast.warning('Set up — but these jobs need authorization', {
          description:
            "They won't run until you authorize them from the Schedule page or your notifications.",
        });
      }
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to set up the automations')),
  });
}

export function useDeclineStarterCrons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ status: string }>('/api/onboarding/day-one/starter-crons/decline', {
        sessionId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dayOneKeys.all }),
    onError: (err) => toast.error(errorMessage(err, 'Failed to record that')),
  });
}

/**
 * FRONT-907: reset the caller's own day-one state (replay path — deletes the
 * seeded chat + the two starter crons it created, nothing else). Used by the
 * dev-only /onboard?replay=1 entry.
 */
export function useResetDayOne() {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-3223: `clearSetupAnswers` — the reworked Re-run also clears the setup answers and style dials.
    mutationFn: (opts?: { clearSetupAnswers?: boolean }) =>
      api.post<{ reset: boolean }>('/api/onboarding/day-one/reset', opts?.clearSetupAnswers ? { clearSetupAnswers: true } : {}),
    onSuccess: () => {
      // Write the cleared state synchronously BEFORE invalidating — the
      // re-entry effects read this cache, and a stale "pending" entry in the
      // window before the refetch lands would re-open the session the reset
      // just deleted (panel finding).
      qc.setQueryData(dayOneKeys.state(), {
        sessionId: null,
        proposalStatus: null,
        chatStatus: null,
        starterCronIds: null,
      });
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
      qc.invalidateQueries({ queryKey: cronKeys.all });
      // FRONT-3223: the reset clears the setup answers and the style dials
      // too, and both are cached for good (`staleTime: Infinity`) — drop them,
      // or the setup would pre-fill the old answers.
      qc.removeQueries({ queryKey: perfectDaySpecKeys.current() });
      qc.removeQueries({ queryKey: calibrationKeys.current() });
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to reset onboarding')),
  });
}

export function useSetDayOneChatStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; status: 'completed' | 'abandoned' }) =>
      api.post<{ status: string }>('/api/onboarding/day-one/status', input),
    onSuccess: (_data, variables) => {
      // Flip the cached chat status synchronously so AppLayout's re-entry
      // guard (which reads this cache on mount) never sees a stale "pending"
      // and bounces the user back into the chat they just left (panel
      // finding). The invalidate then reconciles with the server.
      qc.setQueryData(
        dayOneKeys.state(),
        (old: { chatStatus?: string | null } | undefined) =>
          old ? { ...old, chatStatus: variables.status } : old,
      );
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
    },
  });
}
